import {
  addWeeks,
  endOfMonth,
  endOfToday,
  endOfWeek,
  startOfMonth,
  startOfToday,
  isWithinInterval,
  startOfWeek,
} from 'date-fns';
import { dataFetcher } from 'lib/data-fetcher';
import { NextRouter } from 'next/router';
import { useCallback } from 'react';
import { EventCard, EventTypeData, Filter } from './Types/EventListing.Type';
import { EventListingResponse } from './Types/EventListingResponse.Type';

export const fetchEvents = async ({
  language = 'en', //fallback
  dynamicFilters = [],
  onSuccess,
  onError,
}: {
  language?: string;
  dynamicFilters?: Filter[];
  onSuccess: (data: EventListingResponse[]) => void;
  onError?: () => void;
}) => {
  const baseUrl = process.env['PUBLIC_URL'] || '';
  try {
    dataFetcher<EventListingResponse[]>(`${baseUrl}/api/events/getEventListing`, {
      language,
      dynamicFilters,
    })
      .then((response) => {
        const $data = response.data ?? [];
        onSuccess($data);
      })
      .catch((error) => {
        console.error('Fetch Error:', error);
        onError?.();
      });
  } catch (error) {
    console.error('Unexpected Error:', error);
    onError?.();
  }
};

export const useEventTypeFilter = (router: NextRouter) => {
  const updateFiltersInUrl = useCallback(
    (selectedEventTypes: EventTypeData[], selectedLocationTypes: EventTypeData[], selectedDateFilter: string) => {
      const selectedEventTypeIds = selectedEventTypes.map((t) => t.id);
      const selectedLocationTypeIds = selectedLocationTypes.map((t) => t.id);
      const newQuery = { ...router.query };

      if (selectedEventTypeIds.length > 0) {
        newQuery.eventType = selectedEventTypeIds.join(',');
      } else {
        delete newQuery.eventType;
      }

      if (selectedLocationTypeIds.length > 0) {
        newQuery.locationType = selectedLocationTypeIds.join(',');
      } else {
        delete newQuery.locationType;
      }

      if (selectedDateFilter) {
        newQuery.date = selectedDateFilter;
      } else {
        delete newQuery.date;
      }

      const currentQueryString = JSON.stringify({
        eventType: router.query.eventType,
        locationType: router.query.locationType,
        date: router.query.date,
      });

      const newQueryString = JSON.stringify({
        eventType: newQuery.eventType,
        locationType: newQuery.locationType,
        date: newQuery.date,
      });

      if (currentQueryString === newQueryString) return;

      router.push(
        {
          pathname: router.pathname,
          query: newQuery,
        },
        undefined,
        { shallow: true }
      );
    },
    [router]
  );
  return { updateFiltersInUrl };
};

export const removeFromFilter = (filterChipId: string) => {
  const $element = document.getElementById(filterChipId);
  $element?.click();
};

const isWithinRange = (
  date: Date | string | number,
  startDate: Date | string | number,
  endDate: Date | string | number
) => isWithinInterval(date, { start: startDate, end: endDate });

export const filterEventsByDate = (eventCards: EventCard[], filter: string): EventCard[] => {
  if (!filter || filter === '') return eventCards;

  const today = startOfToday();

  return eventCards.filter((eventcard: EventCard) => {
    const startDate = eventcard.date;
    if (!startDate) return false;
    const date = new Date(startDate);
    switch (filter) {
      case 'today':
        return isWithinRange(date, startOfToday(), endOfToday());
      case 'thisWeek':
        return isWithinRange(date, startOfWeek(today, { weekStartsOn: 1 }), endOfWeek(today, { weekStartsOn: 1 }));
      case 'nextWeek':
        return isWithinRange(
          date,
          startOfWeek(addWeeks(today, 1), { weekStartsOn: 1 }),
          endOfWeek(addWeeks(today, 1), { weekStartsOn: 1 })
        );
      case 'thisMonth':
        return isWithinRange(date, startOfMonth(today), endOfMonth(today));
      default:
        return true;
    }
  });
};
